import React, { useState } from 'react'
import {
  Alert,
  Grid2 as Grid,
  InputAdornment,
  Modal,
  ModalProps,
  Typography
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { healthDataContent } from '../data/health_data_content'
import { InputMask } from 'components/InputMask'
import ButtonBase from 'design-system/button'
import { patchPatientVitalSigns } from 'services/patient'
import { useToast } from 'services/context/toast'
import TimePicker from './timePicker'
import SelectBox from 'components/SelectBox'

interface ManualInputProps {
  visible?: boolean
  onClose?: ModalProps['onClose']
  onSave?: () => void
}

const ManualInput: React.FC<ManualInputProps> = ({
  visible,
  onClose,
  onSave
}) => {
  const { patientId, data } = useParams<{
    patientId: string
    data: string
  }>()
  const healthDataInfo = healthDataContent[data!]

  const toast = useToast()

  const [state, setState] = useState<IStateStdTypes>('idle')

  const [value, setValue] = useState<string>('')
  const sleepTypeControl = useState<string>('total')

  const [sleepType, setSleepType] = sleepTypeControl

  const [error, setError] = useState<string | boolean>()
  const [focus, setFocus] = useState<boolean>(false)

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key == 'Enter') {
      event.preventDefault()
      handleSave()
    }
  }

  const handleSave = async () => {
    const isNotValidate = healthDataInfo.manualInputGuide?.validate(value)
    setError(isNotValidate)
    if (isNotValidate) {
      return false
    }
    setState('loading')
    const response = await patchPatientVitalSigns(
      patientId ?? '',
      healthDataInfo.manualInputGuide?.getJson(value, sleepType)
    )

    if (response.success) {
      setValue('')
      toast.handler({
        content: 'Sinal vital cadastrado com sucesso!'
      })
      if (onSave) onSave()
      setState('success')
      handleCancel({}, 'backdropClick')
    } else {
      setState('error')
    }
  }

  const handleCancel = (
    event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    setError(false)
    setValue('')
    setFocus(false)
    setState('idle')
    setSleepType('total')
    if (onClose) onClose(event, reason)
  }

  const handleChange = (_: any, val: any) => {
    if (state !== 'loading') setValue(val)
  }

  return (
    <Modal
      open={!!visible}
      onClose={handleCancel}
      keepMounted
      slotProps={{
        backdrop: {
          open: true
        }
      }}
    >
      <Grid
        container
        spacing={2}
        flexDirection={'column'}
        size={{
          md: 5,
          lg: 8
        }}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          p: 4,
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant='body1' fontSize={'16pt'}>
          Adicionar {healthDataInfo.title.toLocaleLowerCase()}:
        </Typography>
        {healthDataInfo.manualInputGuide?.isTime ? (
          <Grid
            flexDirection={'row'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'center'}
            gap={2}
          >
            <Grid>
              <SelectBox
                menuItems={[
                  { label: 'Total', value: 'total' },
                  { label: 'REM', value: 'rem' },
                  { label: 'Leve', value: 'light' },
                  { label: 'Profundo', value: 'deep' },
                  { label: 'Acordado', value: 'awake' }
                ]}
                sx={{
                  minWidth: '130px',
                  textAlign: 'left'
                }}
                control={sleepTypeControl}
              ></SelectBox>
            </Grid>
            <TimePicker
              minutes={value}
              onChange={minutes => setValue(minutes)}
              onEnter={handleSave}
            />
          </Grid>
        ) : (
          <InputMask
            label={healthDataInfo.title}
            placeholder={healthDataInfo.manualInputGuide?.placeholder}
            mask={healthDataInfo.manualInputGuide?.mask ?? ''}
            value={value}
            onChange={handleChange}
            error={!!error}
            onFocus={() => {
              if (!focus) setFocus(true)
            }}
            onBlur={() => {
              if (focus) setFocus(false)
            }}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='end'>
                    {(value || focus) && healthDataInfo.dataOptions?.type}
                  </InputAdornment>
                )
              }
            }}
          ></InputMask>
        )}
        {(state === 'error' || !!error) && (
          <Alert
            severity='error'
            sx={{
              flex: 1
            }}
          >
            {error ||
              'Houve um erro ao tentar cadastrar seus dados. Tente novamente.'}
          </Alert>
        )}
        <Grid
          container
          alignSelf={healthDataInfo.manualInputGuide?.isTime ? 'center' : 'end'}
          justifyContent={'end'}
        >
          <ButtonBase
            variant='outlined'
            onClick={() => handleCancel({}, 'backdropClick')}
          >
            {value == '' ? 'Fechar' : 'Cancelar'}
          </ButtonBase>
          <ButtonBase
            onClick={handleSave}
            disabled={value == '' || state == 'loading'}
            loading={state == 'loading'}
          >
            Salvar
          </ButtonBase>
        </Grid>
      </Grid>
    </Modal>
  )
}

export default ManualInput
